import { useMemo } from 'react';

import { Delete, Edit } from '@mui/icons-material';
import { Box, Button, IconButton, Stack, ThemeProvider, Tooltip } from '@mui/material';
import { MaterialReactTable } from 'material-react-table';

import tableThemeCustom from '../../form/tableThemeCustom';
import CustomDialog from '../../layout/CustomDialog';
import Checkbox from '../../form/Checkbox';
import Input from '../../form/Input';
import SelectEmpresa from '../../form/SelectEmpresa';

import SubGrupoDialog from './tab/subGrupo';

function formatarDataHora(value) {
    if (!value) return '';

    const data = new Date(value);
    return Number.isNaN(data.getTime()) ? '' : data.toLocaleString('pt-BR');
}

function simNao(value) {
    return value ? 'Sim' : 'Não';
}

function Form({
    dados,
    handleSubmit,
    handleChange,
    handleOnBlur,
    btnText,
    optionsEmpresa,
    onVoltar,
    subGrupos,
    getSubGrupoRowId,
    mrtDefault,
    dialogSubGrupo,
    subGrupoExcluir,
    subGrupoLoading,
    onNovoSubGrupo,
    onEditarSubGrupo,
    onFecharSubGrupo,
    onConfirmarSubGrupo,
    onSolicitarExcluirSubGrupo,
    onCancelarExcluirSubGrupo,
    onConfirmarExcluirSubGrupo,
    podeIncluirSubGrupo,
    podeEditarSubGrupo,
    podeExcluirSubGrupo,
}) {
    const theme = tableThemeCustom();

    const columns = useMemo(
        () => [
            {
                id: 'subgrupos',
                header: 'SUBGRUPOS',
                columns: [
                    {
                        accessorKey: 'descricao',
                        header: 'Descrição',
                    },
                    {
                        accessorKey: 'ativo',
                        header: 'Ativo',
                        Cell: ({ cell }) => simNao(cell.getValue()),
                    },
                    {
                        accessorKey: 'updatedAt',
                        header: 'Atualizado em',
                        Cell: ({ cell }) => formatarDataHora(cell.getValue()),
                    },
                ],
            },
        ],
        []
    );

    const submit = (event) => {
        event.preventDefault();
        handleSubmit(dados);
    };

    return (
        <div>
            <form onSubmit={submit}>
                <h1>Grupo</h1>

                <div>
                    <SelectEmpresa
                        text="Selecione a Empresa"
                        name="empresaId"
                        options={optionsEmpresa}
                        handleOnChange={handleChange}
                        handleOnBlur={handleOnBlur}
                        value={dados.empresaId || ''}
                        required
                        notOptNull
                        inputWidth="250px"
                    />
                    <Input
                        text="Descrição"
                        type="text"
                        name="descricao"
                        placeholder="Grupo"
                        inputWidth="250px"
                        title="Digite o nome do grupo"
                        handleOnChange={handleChange}
                        handleOnBlur={handleOnBlur}
                        value={dados.descricao || ''}
                        required
                    />
                    <Checkbox
                        title="Indica que esse grupo usa a rotina de produtos"
                        text="Produto"
                        name="produto"
                        handleOnChange={handleChange}
                        value={dados.produto}
                        checked={Boolean(dados.produto)}
                    />
                    <Checkbox
                        title="Indica que esse grupo usa a rotina de centro de custos"
                        text="Centro Custo"
                        name="centroCusto"
                        handleOnChange={handleChange}
                        value={dados.centroCusto}
                        checked={Boolean(dados.centroCusto)}
                    />
                    <Checkbox
                        title="Ativa quando marcado"
                        text="Ativo"
                        name="ativo"
                        handleOnChange={handleChange}
                        value={dados.ativo}
                        checked={Boolean(dados.ativo)}
                    />
                </div>

                <h1>Subgrupos</h1>

                <ThemeProvider theme={theme}>
                    <MaterialReactTable
                        {...mrtDefault}
                        columns={columns}
                        data={subGrupos}
                        getRowId={getSubGrupoRowId}
                        enableRowActions
                        enableRowSelection={false}
                        manualFiltering={false}
                        manualPagination={false}
                        manualSorting={false}
                        rowCount={subGrupos.length}
                        renderTopToolbarCustomActions={() => (
                            <Tooltip arrow placement="top-end" title="Novo subgrupo">
                                <span>
                                    <Button
                                        type="button"
                                        color="secondary"
                                        onClick={onNovoSubGrupo}
                                        variant="contained"
                                        disabled={!podeIncluirSubGrupo || subGrupoLoading}
                                    >
                                        Novo
                                    </Button>
                                </span>
                            </Tooltip>
                        )}
                        renderRowActions={({ row }) => (
                            <Box sx={{ display: 'flex', justifyContent: 'center', gap: 1 }}>
                                <Tooltip arrow placement="top" title="Editar">
                                    <span>
                                        <IconButton
                                            type="button"
                                            onClick={() => onEditarSubGrupo(row.original)}
                                            disabled={!podeEditarSubGrupo || subGrupoLoading}
                                        >
                                            <Edit />
                                        </IconButton>
                                    </span>
                                </Tooltip>
                                <Tooltip arrow placement="right" title="Excluir">
                                    <span>
                                        <IconButton
                                            type="button"
                                            color="error"
                                            onClick={() => onSolicitarExcluirSubGrupo(row.original)}
                                            disabled={!podeExcluirSubGrupo || subGrupoLoading}
                                        >
                                            <Delete />
                                        </IconButton>
                                    </span>
                                </Tooltip>
                            </Box>
                        )}
                    />
                </ThemeProvider>

                <Stack direction="row" spacing={1} sx={{ mt: 2 }}>
                    <Button type="button" variant="contained" color="secondary" onClick={onVoltar}>
                        Voltar
                    </Button>
                    <Button type="submit" variant="contained" color="success">
                        {btnText}
                    </Button>
                </Stack>
            </form>

            <SubGrupoDialog
                open={dialogSubGrupo.open}
                selected={dialogSubGrupo.selected}
                loading={subGrupoLoading}
                onClose={onFecharSubGrupo}
                onConfirm={onConfirmarSubGrupo}
            />

            <CustomDialog
                open={Boolean(subGrupoExcluir)}
                title="Excluir Subgrupo"
                content={
                    <Box>
                        Confirma a exclusão do subgrupo <strong>{subGrupoExcluir?.descricao}</strong>?
                    </Box>
                }
                onClose={onCancelarExcluirSubGrupo}
                onConfirm={onConfirmarExcluirSubGrupo}
                confirmLabel="Excluir"
                confirmColor="error"
                loading={subGrupoLoading}
            />
        </div>
    );
}

export default Form;
